// Generat automat din README.md - Planificator concedii pentru Margarit Alexandru Florian
import React, { useState } from 'react';
import { Calendar, User, Clock, CheckCircle, XCircle, Plus, MapPin, Plane, Sun, Coffee } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import { mockData } from '../../utils/mockData';

const PlanificatorConcedii = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newVacationForm, setNewVacationForm] = useState({
    startDate: '',
    endDate: '',
    type: 'Concediu de odihnă',
    reason: '',
    notes: ''
  });

  // Concediile pentru Margarit Alexandru Florian
  const margaritVacations = [
    {
      id: 1,
      employee: 'Margarit Alexandru Florian',
      startDate: '2024-07-15',
      endDate: '2024-07-29',
      days: 14,
      status: 'Planificat',
      type: 'Concediu de odihnă',
      reason: 'Concediu de vară planificat',
      requestDate: '2024-02-20',
      approvedBy: 'Auto-aprobat (Proprietar)',
      notes: 'Perioada de vară - activitate redusă comenzi'
    },
    {
      id: 2,
      employee: 'Margarit Alexandru Florian',
      startDate: '2024-04-29',
      endDate: '2024-05-03',
      days: 5,
      status: 'Aprobat',
      type: 'Concediu de Paște',
      reason: 'Sărbători religioase',
      requestDate: '2024-03-01',
      approvedBy: 'Auto-aprobat (Proprietar)',
      notes: 'Concediu pentru Sărbătorile de Paște'
    },
    {
      id: 3,
      employee: 'Margarit Alexandru Florian',
      startDate: '2024-12-23',
      endDate: '2024-01-02',
      days: 10,
      status: 'Planificat',
      type: 'Concediu de Crăciun',
      reason: 'Sărbători de iarnă',
      requestDate: '2024-10-15',
      approvedBy: 'Auto-aprobat (Proprietar)',
      notes: 'Perioada sărbătorilor de iarnă - afacerea închisă'
    },
    {
      id: 4,
      employee: 'Margarit Alexandru Florian',
      startDate: '2024-03-25',
      endDate: '2024-03-25',
      days: 1,
      status: 'Finalizat',
      type: 'Zi liberă medicală',
      reason: 'Control medical anual',
      requestDate: '2024-03-20',
      approvedBy: 'Auto-aprobat (Proprietar)',
      notes: 'Control medical preventiv'
    }
  ];

  const handleSubmitVacation = () => {
    // Logica pentru adăugarea unei noi cereri de concediu
    console.log('New vacation request:', newVacationForm);
    setIsModalOpen(false);
    // Reset form
    setNewVacationForm({
      startDate: '',
      endDate: '',
      type: 'Concediu de odihnă',
      reason: '',
      notes: ''
    });
  };

  const calculateDays = () => {
    if (newVacationForm.startDate && newVacationForm.endDate) {
      const start = new Date(newVacationForm.startDate);
      const end = new Date(newVacationForm.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Aprobat': return 'success';
      case 'Planificat': return 'info';
      case 'Finalizat': return 'default';
      case 'Respins': return 'error';
      default: return 'warning';
    }
  };

  const columns = [
    { 
      header: 'Angajat', 
      key: 'employee',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
            MF
          </div>
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    { header: 'Data început', key: 'startDate' },
    { header: 'Data sfârșit', key: 'endDate' },
    { 
      header: 'Zile', 
      key: 'days',
      render: (value) => (
        <span className="font-semibold text-blue-600">{value} zile</span>
      )
    },
    { 
      header: 'Tip', 
      key: 'type',
      render: (value) => (
        <Badge variant="info">{value}</Badge>
      )
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    }
  ];

  // Calcule pentru statistici
  const totalDaysThisYear = margaritVacations.reduce((acc, vacation) => acc + vacation.days, 0);
  const approvedVacations = margaritVacations.filter(v => v.status === 'Aprobat' || v.status === 'Finalizat').length;
  const plannedVacations = margaritVacations.filter(v => v.status === 'Planificat').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Calendar className="w-8 h-8 text-blue-500" />
            Planificator Concedii - Margarit Alexandru Florian
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gestionarea personalizată a concediilor pentru proprietarul afacerii
          </p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Planifică Concediu
        </button>
      </div>

      {/* Statistici */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card title="Total Zile 2024" icon={Sun} variant="stats">
          <div className="text-3xl font-bold text-blue-600">{totalDaysThisYear}</div>
          <p className="text-sm text-gray-500 mt-1">Zile de concediu</p>
        </Card>

        <Card title="Concedii Aprobate" icon={CheckCircle} variant="stats">
          <div className="text-3xl font-bold text-green-600">{approvedVacations}</div>
          <p className="text-sm text-gray-500 mt-1">Finalizate/Aprobate</p>
        </Card>

        <Card title="Planificate" icon={Clock} variant="stats">
          <div className="text-3xl font-bold text-yellow-600">{plannedVacations}</div>
          <p className="text-sm text-gray-500 mt-1">În planificare</p>
        </Card>

        <Card title="Flexibilitate" icon={Coffee} variant="stats">
          <div className="text-3xl font-bold text-purple-600">100%</div>
          <p className="text-sm text-gray-500 mt-1">Control total program</p>
        </Card>
      </div>

      {/* Informații despre proprietar */}
      <Card>
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-start">
            <User className="w-5 h-5 text-green-500 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                Avantajele Proprietarului
              </h3>
              <p className="mt-2 text-sm text-green-700 dark:text-green-300">
                Ca proprietar al afacerii <strong>"Botez de poveste"</strong>, Margarit Alexandru Florian are 
                flexibilitate completă în planificarea concediilor. Poate adapta programul de lucru în funcție 
                de sezonalitatea comenzilor și poate programa concediile în perioadele cu activitate redusă.
              </p>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tabel concedii */}
        <div className="lg:col-span-2">
          <Card title="Concediile lui Margarit Alexandru Florian" subtitle="Istoric și planificare 2024">
            <Table 
              columns={columns} 
              data={margaritVacations}
              onRowClick={(vacation) => {
                // Poți adăuga logică pentru vedere detalii
                console.log('Selected vacation:', vacation);
              }}
            />
          </Card>
        </div>

        {/* Sidebar cu calendar și acțiuni */}
        <div className="space-y-6">
          {/* Calendar placeholder */}
          <Card title="Calendar Concedii 2024" icon={Calendar}>
            <div className="h-64 bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Calendar className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 font-medium">Calendar Interactiv</p>
                <p className="text-sm text-gray-500 mt-2">Vizualizare concedii pe luni</p>
                <div className="mt-4 space-y-1">
                  <div className="text-xs text-gray-400">Următorul concediu:</div>
                  <div className="text-sm font-medium text-blue-600">15 Iulie 2024</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Acțiuni rapide */}
          <Card title="Acțiuni Rapide">
            <div className="space-y-3">
              <button className="w-full btn-primary text-sm flex items-center gap-2 justify-center">
                <Plane className="w-4 h-4" />
                Planifică Vacanță
              </button>
              <button className="w-full btn-secondary text-sm flex items-center gap-2 justify-center">
                <MapPin className="w-4 h-4" />
                Verifică Disponibilitate
              </button>
              <button className="w-full btn-secondary text-sm">
                Exportă Calendar PDF
              </button>
              <button className="w-full btn-secondary text-sm">
                Setări Notificări
              </button>
            </div>
          </Card>

          {/* Sfaturi pentru planning */}
          <Card title="Sfaturi Planning">
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-400">
                  <strong>Vară:</strong> Planifică concedii lungi în iulie-august când cererea este mai mică
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-400">
                  <strong>Sărbători:</strong> Închide afacerea în perioadele religioase importante
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-400">
                  <strong>Flexibilitate:</strong> Adaptează programul în funcție de comenzile mari
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Modal pentru planificare concediu nou */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Planifică Concediu Nou - Margarit Alexandru Florian"
        size="lg"
      >
        <div className="space-y-6">
          {/* Info proprietar */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                MF
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 dark:text-blue-100">
                  Margarit Alexandru Florian
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Proprietar & Manager General
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Data de început *
              </label>
              <input
                type="date"
                value={newVacationForm.startDate}
                onChange={(e) => setNewVacationForm({...newVacationForm, startDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Data de sfârșit *
              </label>
              <input
                type="date"
                value={newVacationForm.endDate}
                onChange={(e) => setNewVacationForm({...newVacationForm, endDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>

          {calculateDays() > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
              <p className="text-green-800 dark:text-green-200 font-medium">
                Total zile: {calculateDays()} zile
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">
              Tipul concediului
            </label>
            <select
              value={newVacationForm.type}
              onChange={(e) => setNewVacationForm({...newVacationForm, type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="Concediu de odihnă">Concediu de odihnă</option>
              <option value="Concediu medical">Concediu medical</option>
              <option value="Concediu religios">Concediu religios</option>
              <option value="Zi liberă">Zi liberă</option>
              <option value="Vacanță planificată">Vacanță planificată</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Motivul concediului
            </label>
            <input
              type="text"
              value={newVacationForm.reason}
              onChange={(e) => setNewVacationForm({...newVacationForm, reason: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="ex: Vacanță de vară cu familia"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Note suplimentare
            </label>
            <textarea
              value={newVacationForm.notes}
              onChange={(e) => setNewVacationForm({...newVacationForm, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              rows="3"
              placeholder="Detalii suplimentare, aranjamente speciale pentru afacere..."
            />
          </div>

          {/* Footer modal */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              Anulează
            </button>
            <button
              onClick={handleSubmitVacation}
              disabled={!newVacationForm.startDate || !newVacationForm.endDate}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Planifică Concediul
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PlanificatorConcedii;